import { useEffect, useState } from 'react'
import { Check, X, KeyRound, LogOut, Shield, UserCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { platformPermissionGroups } from '@/security/permissionPresentation'
import { useAuth } from '@/contexts/AuthContext'
import { getRoleDescriptionFromContract, getRoleLabel } from '@/security/accessContract'
import { platformApi, type MfaMethod, type TotpSetup } from '@/services/platform/platform.http'

export function PlatformProfilePage() {
  const { user, hasPermission } = useAuth()
  if (!user) return null
  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">My Profile</h1>
        <p className="text-text-secondary">Your platform identity and effective access.</p>
      </header>
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="card p-6">
          <div className="mb-5 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <UserCircle className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                {user.fullName || user.email}
              </h2>
              <p className="text-sm text-text-secondary">{user.email}</p>
            </div>
          </div>
          <dl className="grid grid-cols-[8rem_1fr] gap-3 text-sm">
            <dt className="text-text-secondary">User ID</dt>
            <dd className="break-all">{user.id}</dd>
            <dt className="text-text-secondary">Role</dt>
            <dd>{getRoleLabel(user.role)}</dd>
            <dt className="text-text-secondary">Scope</dt>
            <dd>Tenantless platform identity</dd>
            <dt className="text-text-secondary">Verified</dt>
            <dd>{user.isVerified ? 'Yes' : 'No'}</dd>
          </dl>
          {hasPermission('txh.platform.user.update') && (
            <Link className="btn btn-primary mt-5" to={`/platform/users/${user.id}`}>
              Edit account details
            </Link>
          )}
        </section>
        <section className="card p-6">
          <div className="mb-3 flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">Access summary</h2>
          </div>
          <p className="mb-4 text-sm text-text-secondary">
            {getRoleDescriptionFromContract(user.role)}
          </p>
          <p className="mb-3 text-sm text-text-secondary">
            A check marks an action available in this session. A cross marks an action you cannot
            perform.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-overlay">
                <tr>
                  <th className="px-3 py-3 text-left">Area</th>
                  <th className="px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-overlay">
                {platformPermissionGroups(user.permissions ?? []).map(group => (
                  <tr key={group.area}>
                    <th
                      scope="row"
                      className="px-3 py-3 text-left align-top font-medium text-text-primary"
                    >
                      {group.area}
                    </th>
                    <td className="px-3 py-3">
                      <ul className="space-y-2">
                        {group.actions.map(action => (
                          <li key={action.label} className="flex items-center gap-2">
                            {action.allowed ? (
                              <Check
                                aria-hidden="true"
                                className="h-4 w-4 text-green-700 dark:text-green-400"
                              />
                            ) : (
                              <X aria-hidden="true" className="h-4 w-4 text-text-tertiary" />
                            )}
                            <span
                              className={
                                action.allowed ? 'text-text-primary' : 'text-text-secondary'
                              }
                            >
                              {action.label}
                            </span>
                            <span className="sr-only">
                              {action.allowed ? 'Allowed' : 'Not allowed'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  )
}

export function PlatformSettingsPage() {
  const { user, logout } = useAuth()
  const [method, setMethod] = useState<MfaMethod | null>(null)
  const [setup, setSetup] = useState<TotpSetup | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  useEffect(() => {
    platformApi
      .mfaMethods()
      .then(items => setMethod(items.find(item => item.method === 'totp') ?? null))
      .catch(value =>
        setError(value instanceof Error ? value.message : 'Unable to load MFA status')
      )
  }, [])
  const beginSetup = async () => {
    setError('')
    try {
      setSetup(await platformApi.setupTotp())
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Unable to start MFA setup')
    }
  }
  const confirmSetup = async () => {
    setError('')
    try {
      await platformApi.verifyTotpSetup(code)
      setMethod({ method: 'totp', enabled: true, verified: true })
      setSetup(null)
      setCode('')
      setNotice(
        'MFA enabled. Sign out, then sign in with an authenticator code before requesting tenant support access.'
      )
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Invalid authenticator code')
    }
  }
  if (!user) return null
  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Account Settings</h1>
        <p className="text-text-secondary">
          Security, session, and access-contract controls for your platform account.
        </p>
      </header>
      {error && (
        <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">{notice}</div>
      )}
      <div className="space-y-5">
        <section className="card p-6">
          <div className="flex items-start gap-3">
            <KeyRound className="mt-0.5 h-5 w-5 text-primary" />
            <div className="w-full">
              <h2 className="font-semibold">Multi-factor authentication</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Required for temporary tenant support access. Support requests require a login
                verified with MFA within the previous five minutes.
              </p>
              {method?.enabled ? (
                <div className="mt-4">
                  <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                    TOTP enabled
                  </span>
                  <p className="mt-3 text-sm text-text-secondary">
                    To obtain a fresh support authorization, sign out and sign back in with the
                    authenticator code field.
                  </p>
                </div>
              ) : !setup ? (
                <button className="btn btn-primary mt-4" onClick={() => void beginSetup()}>
                  Set up authenticator
                </button>
              ) : (
                <div className="mt-4 space-y-4">
                  <div>
                    <p className="text-sm font-medium">
                      1. Add this account to your authenticator app
                    </p>
                    <code className="mt-1 block break-all rounded bg-surface-overlay p-3 text-xs">
                      {setup.secret}
                    </code>
                    <p className="mt-1 break-all text-xs text-text-tertiary">{setup.qr_code_url}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      2. Store these one-time backup codes securely
                    </p>
                    <div className="mt-1 grid grid-cols-2 gap-1 rounded bg-surface-overlay p-3 font-mono text-xs">
                      {setup.backup_codes.map(backup => (
                        <span key={backup}>{backup}</span>
                      ))}
                    </div>
                  </div>
                  <label className="block text-sm">
                    3. Enter the current authenticator code
                    <input
                      className="input mt-1 w-full"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={e => setCode(e.target.value.trim())}
                    />
                  </label>
                  <button
                    className="btn btn-primary"
                    disabled={code.length < 6}
                    onClick={() => void confirmSetup()}
                  >
                    Enable MFA
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
        <section className="card p-6">
          <div className="flex items-start gap-3">
            <Shield className="mt-0.5 h-5 w-5 text-primary" />
            <div>
              <h2 className="font-semibold">Access contract</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Version {user.accessContractVersion ?? 'not reported'} · role{' '}
                {getRoleLabel(user.role)}
              </p>
              <p className="mt-2 text-sm text-text-secondary">
                Role or permission changes take effect when a new access token is issued. Sign out
                and sign back in after an administrator changes your access.
              </p>
            </div>
          </div>
        </section>
        <section className="card border border-red-200 p-6">
          <h2 className="font-semibold text-red-700">Sessions</h2>
          <p className="my-2 text-sm text-text-secondary">
            Sign out this browser, or revoke all sessions for this account if you suspect
            unauthorized access.
          </p>
          <div className="flex flex-wrap gap-2">
            <button className="btn" onClick={() => void logout(false)}>
              <LogOut className="h-4 w-4" /> Sign out
            </button>
            <button className="btn btn-error" onClick={() => void logout(true)}>
              Sign out all devices
            </button>
          </div>
        </section>
      </div>
    </>
  )
}
